import { createFileRoute } from '@tanstack/react-router';
import { PageHero } from '../components/ui/hero.js';
import { CompareScene } from '../components/mascot/scenes.js';
import { breadcrumbSchema, jsonLd, seo } from '../lib/seo.js';
import {
  COMPETITORS,
  comparisonPath,
  oldestVerification,
} from '../content/competitors.js';
import { WIDGET_BUILDER_PATH } from '../content/widgets.js';
import { Measure, Section } from '../components/ui/layout.js';
import { Eyebrow, Heading, Mark, Text } from '../components/ui/type.js';
import { ButtonLink, SiteLink } from '../components/ui/button.js';

/**
 * The comparison hub.
 *
 * Exists because the header has linked `/compare` since the navigation phase
 * and nothing was there — a dead end in the primary nav, which the funnel audit
 * would have found even if this phase had not.
 *
 * Deliberately short. A hub's job is to route, and the pages it routes to are
 * where the argument happens.
 */
export const Route = createFileRoute('/compare/')({
  head: () => {
    const { meta, links } = seo({
      title: 'How ReviewsJet compares',
      description:
        'Honest comparisons of ReviewsJet with Senja, Testimonial.to, Elfsight, REVIEWS.io and Trustmary — including what each of them does better. Every fact sourced and dated.',
      path: '/compare',
    });

    return {
      meta,
      links,
      scripts: [
        {
          type: 'application/ld+json',
          children: jsonLd(
            breadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Comparisons', path: '/compare' },
            ])
          ),
        },
      ],
    };
  },
  component: CompareIndex,
});

function CompareIndex() {
  return (
    <>
      <PageHero
        trail={[
          { name: 'Home', path: '/' },
          { name: 'Comparisons', path: '/compare' },
        ]}
        art={<CompareScene />}
      >
        <Measure>
          <Eyebrow>Comparisons</Eyebrow>
          <Heading level={1} size="hero" className="mt-4">
            How ReviewsJet <span className="whitespace-nowrap"><Mark>compares</Mark>.</span>
          </Heading>
          <Text size="lead" className="mt-6">
            ReviewsJet does one thing: import the reviews a business already
            has, manage them, and display them on its website. Several of the
            products below do more than that, and each page says where they are
            the better choice.
          </Text>
          <Text className="mt-4">
            Every competitor fact links to the page it came from and shows the
            date it was checked. If something has changed, the source will show
            it before we do.
          </Text>
        </Measure>
      </PageHero>

      <Section aria-labelledby="list-heading">
        <Heading level={2} id="list-heading" className="sr-only">
          All comparisons
        </Heading>

        {/*
          A list of rows rather than a grid of cards. Five items with a sentence
          each is a list; putting each in a bordered box would be the card-grid
          reflex the design brief specifically rules out.
        */}
        <ul className="max-w-text">
          {COMPETITORS.map((competitor, index) => (
            <li
              key={competitor.slug}
              className={index > 0 ? 'mt-8 border-t border-ink-150 pt-8' : ''}
            >
              <SiteLink
                to={comparisonPath(competitor)}
                className="group block"
              >
                <p className="text-title text-ink-950 group-hover:underline group-hover:decoration-ink-300 group-hover:underline-offset-[3px]">
                  ReviewsJet vs {competitor.name}
                </p>
                <p className="mt-2 max-w-prose text-body-sm text-ink-600">
                  {competitor.summary}
                </p>
                <p className="mt-2.5 text-caption text-ink-500">
                  Checked{' '}
                  <time dateTime={oldestVerification(competitor)}>
                    {new Date(
                      `${oldestVerification(competitor)}T00:00:00Z`
                    ).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      timeZone: 'UTC',
                    })}
                  </time>
                </p>
              </SiteLink>
            </li>
          ))}
        </ul>
      </Section>

      <Section tone="ink" size="base">
        <Measure>
          <Heading level={2} className="text-white">
            Or skip the reading.
          </Heading>
          <Text className="mt-4 text-white/70">
            Build a widget with sample reviews and see whether it looks like
            something you would put on your site. No account needed.
          </Text>
          <div className="mt-8">
            <ButtonLink to={WIDGET_BUILDER_PATH} variant="inverse" size="lg">
              Build a widget free
            </ButtonLink>
          </div>
        </Measure>
      </Section>
    </>
  );
}
